import { Popover, Transition } from '@headlessui/react';
import { IconColorPicker, IconPalette } from '@tabler/icons-react';
import React, { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import { Clip } from '@/domains/asset';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import BrandKitInlineEmptyState from '@/Pages/Clip/SideBar/EmptyStates/BrandKitInlineEmptyState';
import { brandKit } from '@/stores/brandKit';
import { updateSpeakerLabelStyles } from '@/stores/clip';
import { BACKGROUND_COLOR_DEFAULT, TEXT_COLOR_DEFAULT } from '@/stores/constants';

function SpeakerLabelColorsMenu({ clip }: { clip: Clip }) {
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);

  const isAudiogram = useMemo(() => clip.asset_metadata.layout === 'AUDIOGRAM', [clip.asset_metadata.layout]);

  const onTextColorChanged = useCallback(
    (value?: string) => {
      updateSpeakerLabelStyles(clip.id, { text_color: value });
    },
    [clip.id]
  );

  const onBackgroundColorChanged = useCallback(
    (value?: string) => {
      updateSpeakerLabelStyles(clip.id, { background_color: value });
    },
    [clip.id]
  );

  const textColor = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.text_color || TEXT_COLOR_DEFAULT,
    [clip.asset_metadata.speaker_label_styles?.text_color]
  );

  const backgroundColor = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.background_color || BACKGROUND_COLOR_DEFAULT,
    [clip.asset_metadata.speaker_label_styles?.background_color]
  );

  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="speaker-label-inline-colors-button"
            className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconPalette size={20} />
              <PlayerMenuTooltip content="Colors" />
            </div>
          </Popover.Button>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="flex justify-start">
              <div className="absolute right-0 mt-1.5 flex w-48 flex-col items-start justify-start divide-y rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg">
                <div className="flex w-full items-center justify-between px-3 py-1.5">
                  <span>Text Color</span>
                  <div className="relative size-8">
                    <ColorPicker
                      classNames="rounded-2xl flex justify-center items-center"
                      pickerClassNames="right-11 top-[-3rem]"
                      defaultColors={brandKitStore.colors}
                      defaultColorsLabel="Brand Colors"
                      onChange={onTextColorChanged}
                      hex={textColor}
                    >
                      <IconColorPicker size={16} stroke={2} />
                      <BrandKitInlineEmptyState label="colors" />
                    </ColorPicker>
                  </div>
                </div>
                {!isAudiogram && (
                  <div className="flex w-full items-center justify-between px-3 py-1.5">
                    <span>Background</span>
                    <div className="relative size-8">
                      <ColorPicker
                        classNames="rounded-2xl flex justify-center items-center"
                        pickerClassNames="right-11 top-[-3rem]"
                        defaultColors={brandKitStore.colors}
                        defaultColorsLabel="Brand Colors"
                        onChange={onBackgroundColorChanged}
                        hex={backgroundColor}
                      >
                        <IconColorPicker size={16} stroke={2} />
                        <BrandKitInlineEmptyState label="colors" />
                      </ColorPicker>
                    </div>
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(SpeakerLabelColorsMenu);
